import { useMemo } from 'react';

import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import type { SALARY_CALCULATION_OPTION } from 'cpool-constants';
import { SALARY_CALCULATION_OPTIONS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { objectKeys } from 'cpool-utils';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { calculateSalary } from './utils/calculateSalary';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import { getSalaryFromLead } from '../utils/getSalaryFromLead';
import { useOnValueChanged } from '~/hooks/useOnValueChanged.tsx';
import { currencyFormat } from '~/lib/i18n';

type Props = {
    applicant: 1 | 2;
    lead: SchemaLoanRequestResolver_Get;
    disabled?: boolean;
};

const values = objectKeys(SALARY_CALCULATION_OPTIONS);

export const SalarySelect: React.FC<Props> = ({ applicant, disabled, lead }) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext<SchemaCreditRequestForm>();
    const {
        field: { onChange, value: salaryMethod },
    } = useController<
        SchemaCreditRequestForm,
        'dataSnippets.applicant1.salaryMode' | 'dataSnippets.applicant2.salaryMode'
    >({
        name: applicant === 1 ? 'dataSnippets.applicant1.salaryMode' : 'dataSnippets.applicant2.salaryMode',
    });

    const salaryValues = useMemo(() => getSalaryFromLead(lead), [lead]);

    useOnValueChanged({
        onChange: () => {
            setValue(
                applicant === 1 ? 'dataSnippets.applicant1.salary' : 'dataSnippets.applicant2.salary',
                calculateSalary({ applicant, salaryMethod, salaryValues }),
            );
        },
        values: [salaryValues],
    });

    const formatSalary = (value: SALARY_CALCULATION_OPTION) => {
        const salary = calculateSalary({ applicant, salaryMethod: value, salaryValues });
        if (typeof salary === 'number' && !Number.isNaN(salary) && Number.isFinite(salary)) {
            return currencyFormat(salary);
        }
        return t('BANK_CREDIT_REQUEST.DATA_SHARING.INVALID_VALUE');
    };

    const selectChange = (value: SALARY_CALCULATION_OPTION) => {
        onChange(value);
        const newSalary = calculateSalary({ applicant, salaryMethod: value, salaryValues });
        if (typeof newSalary === 'number' && !Number.isNaN(newSalary) && Number.isFinite(newSalary)) {
            setValue(applicant === 1 ? 'dataSnippets.applicant1.salary' : 'dataSnippets.applicant2.salary', newSalary);
        } else {
            setValue(applicant === 1 ? 'dataSnippets.applicant1.salary' : 'dataSnippets.applicant2.salary', undefined);
        }
    };
    return (
        <DropdownMenu<SALARY_CALCULATION_OPTION>
            aria-label={t('BANK_CREDIT_REQUEST.DATA_SHARING.SALARY')}
            data-testid={`salary-select-${applicant}`}
            className="w-full"
            triggerClassName="w-full justify-between"
            options={values.map((option) => ({
                label: `${t(`BANK_CREDIT_REQUEST.DATA_SHARING.SALARY_CALCULATION.${option}`)} (${formatSalary(
                    option,
                )})`,
                value: option,
            }))}
            value={salaryMethod}
            onChange={(value) => selectChange(value as SALARY_CALCULATION_OPTION)}
            disabled={disabled}
        />
    );
};
